import { DeliveryZone, GQL_ID } from '@@types'
import { memo, useEffect, useMemo, useRef } from 'react'

import { type Brand, type GeoPoint, useAddressStore } from '@entities/Address'
import {
  useDeliveryZonesForGeoPoint,
  useSingleDeliveryZone,
} from '@entities/DeliveryZone'
import { useDeliveryStore } from '@entities/DeliveryZone/model/useDeliveryStore'
import { type Slot, useAvailableSlots } from '@entities/Slots'

import { useSlotStore } from '@widgets/slots'

import { SHOP_ID } from '@shared/contants'
import { prefetchBrandIcons } from '@shared/lib/brandIconCache'

/**
 * Менеджер доставки — единственный компонент на весь сайт,
 * который делает запросы и обновляет Zustand store.
 * Рендерит null, только выполняет side-effects.
 */
export const DeliveryManager = memo(function DeliveryManager () {
  // Читаем из address store через селекторы
  const selectedAddress = useAddressStore((state) => state.selectedAddress)
  const shopId = useAddressStore((state) => state.shopId)
  const zoneId = useAddressStore((state) => state.zoneId)
  const brand = useAddressStore((state) => state.brand)
  const geoPoint = useAddressStore((state) => state.geoPoint)
  const deliveryType = useAddressStore((state) => state.deliveryType)

  const selectedSlot = useSlotStore((state) => state.selectedSlot)
  const setSelectedSlot = useSlotStore((state) => state.setSelectedSlot)

  // Функция для обновления delivery store
  const updateDeliveryData = useDeliveryStore((state) => state.updateDeliveryData)

  // Ref для отслеживания последней синхронизации
  const lastSyncRef = useRef<{ zoneId: GQL_ID, shopId: GQL_ID, brandId: GQL_ID }>({ zoneId: '', shopId: '', brandId: '' })

  // Используем geoPoint из стора или из адреса
  const currentGeoPoint = useMemo<GeoPoint | null>(() => {
    return geoPoint || selectedAddress?.customerGeoPoint || null
  }, [ geoPoint, selectedAddress?.customerGeoPoint ])

  // Запрос зон для текущего адреса
  const {
    deliveryZones,
    loading: loadingZones,
  } = useDeliveryZonesForGeoPoint({
    point: currentGeoPoint,
    skip: !currentGeoPoint?.latitude || !currentGeoPoint?.longitude,
  })

  // Запрос текущей зоны по ID
  const { deliveryZone: singleZone } = useSingleDeliveryZone({
    id: zoneId ? zoneId : undefined,
    skip: !zoneId,
  })

  // Запрос слотов
  const {
    slots,
    loading: loadingSlots,
  } = useAvailableSlots({
    shopId: deliveryType === 'pickup' && shopId ? String(shopId) : undefined,
    zoneId: deliveryType !== 'pickup' && zoneId ? String(zoneId) : undefined,
    pollInterval: 60000,
  })

  // Вычисляем все значения
  const computedData = useMemo(() => {
    // Проверяем есть ли доставка
    const hasDelivery = deliveryZones.some(
      (zone) => !!zone.firstAvailableSlot?.id || !!zone.isExpressDeliveryAvailable,
    )

    // Доступные бренды
    const brandsMap = new Map<string, {
      brandInfo: Brand
      nearestSlot: Slot | null
      delivery_cost: number | null
    }>()

    deliveryZones.forEach((zone) => {
      const zoneBrand = zone.shop?.brand
      const brandId = zoneBrand?.id != null ? String(zoneBrand.id) : null
      if (zoneBrand && brandId && !brandsMap.has(brandId)) {
        brandsMap.set(brandId, {
          brandInfo: zoneBrand,
          nearestSlot: zone.firstAvailableSlot || null,
          delivery_cost: zone.delivery_cost ?? null,
        })
      }
    })
    const availableBrands = Array.from(brandsMap.values())

    // Текущая зона
    let currentZone: DeliveryZone | null = null
    if (singleZone) {
      currentZone = singleZone
    }
    else if (deliveryZones.length > 0) {
      if (zoneId) {
        currentZone = deliveryZones.find((z) => z.id === zoneId) ?? deliveryZones[0]
      }
      else if (brand) {
        currentZone = deliveryZones.find((z) => z.shop?.brand?.id === brand.id) ?? deliveryZones[0]
      }
      else {
        currentZone = deliveryZones[0]
      }
    }

    // Параметры доставки
    const deliveryCost = currentZone?.delivery_cost ?? 0
    const minOrderCost = currentZone?.minOrderCost ?? 0
    const freeSlotDeliveryCostFrom = currentZone?.freeSlotDeliveryCostFrom ?? 0
    const isExpressDeliveryAvailable = !!currentZone?.isExpressDeliveryAvailable
    const isSlotDeliveryAvailable = slots.length > 0
    const maxSlotWeight = currentZone?.zoneSlotMaxWeight ?? 0
    const maxExpressWeight = currentZone?.zoneExpressMaxWeight ?? 0

    return {
      deliveryZones,
      currentZone,
      availableBrands,
      slots,
      hasDelivery,
      isSlotDeliveryAvailable,
      isExpressDeliveryAvailable,
      deliveryCost,
      minOrderCost,
      freeSlotDeliveryCostFrom,
      maxSlotWeight,
      maxExpressWeight,
    }
  }, [ deliveryZones, singleZone, zoneId, brand, slots ])

  const availableBrands = computedData.availableBrands

  useEffect(() => {
    prefetchBrandIcons(availableBrands.map((item) => item.brandInfo))
  }, [ availableBrands ])

  // Обновляем store при изменении данных
  useEffect(() => {
    updateDeliveryData({
      ...computedData,
      loading: loadingZones || loadingSlots,
      loadingZones,
      loadingSlots,
    })
  }, [ computedData, loadingZones, loadingSlots, updateDeliveryData ])

  // Автоматическая синхронизация zone с brand
  useEffect(() => {
    if (deliveryType === 'pickup') return
    if (loadingZones) return
    
    const state = useAddressStore.getState()
    
    // Если зон нет, но есть адрес - сбрасываем зоны
    if (deliveryZones.length === 0 && state.selectedAddress) {
      if (state.zoneId) state.setZoneId('')
      if (state.shopId && state.shopId !== SHOP_ID) state.setShopId(SHOP_ID)
      if (state.brand) state.setBrand(null)
      lastSyncRef.current = { zoneId: '', shopId: SHOP_ID, brandId: '' }
      return
    }
    
    if (deliveryZones.length === 0) return

    const currentZoneId = state.zoneId
    const currentShopId = state.shopId
    const currentBrand = state.brand
    
    const lastSyncKey = `${lastSyncRef.current.zoneId}-${lastSyncRef.current.shopId}-${lastSyncRef.current.brandId}`

    const currentZone = currentZoneId
      ? deliveryZones.find((z) => z.id === currentZoneId) ?? null
      : null

    // Если бренд не сохранен, но у текущей зоны он есть — синхронизируем
    if (!currentBrand && currentZone?.shop?.brand) {
      const newSyncKey = `${currentZone.id}-${currentZone.shop_id || ''}-${currentZone.shop?.brand?.id || ''}`
      if (newSyncKey !== lastSyncKey) {
        lastSyncRef.current = {
          zoneId: currentZone.id,
          shopId: currentZone.shop_id || '',
          brandId: String(currentZone.shop?.brand?.id || ''),
        }
        state.setBrand(currentZone.shop.brand as Brand)
        if (currentZone.shop_id && String(currentZone.shop_id) !== String(currentShopId)) {
          state.setShopId(String(currentZone.shop_id))
        }
      }
    }

    // Проверяем существует ли текущая зона в списке
    const currentZoneExists = Boolean(currentZone)

    // Если текущей зоны нет в списке
    if (currentZoneId && !currentZoneExists) {
      const zoneToSet = currentBrand
        ? deliveryZones.find((z) => String(z.shop?.brand?.id) === String(currentBrand.id))
        : deliveryZones[0]
      
      if (zoneToSet) {
        const newSyncKey = `${zoneToSet.id}-${zoneToSet.shop_id || ''}-${zoneToSet.shop?.brand?.id || ''}`
        if (newSyncKey !== lastSyncKey) {
          lastSyncRef.current = { 
            zoneId: zoneToSet.id, 
            shopId: zoneToSet.shop_id || '', 
            brandId: String(zoneToSet.shop?.brand?.id || '') 
          }
          state.setZoneId(zoneToSet.id)
          if (zoneToSet.shop_id) state.setShopId(String(zoneToSet.shop_id))
          if (zoneToSet.shop?.brand) state.setBrand(zoneToSet.shop.brand as Brand)
        }
      }
      return
    }

    // Если только одна зона
    if (deliveryZones.length === 1) {
      const zone = deliveryZones[0]
      const needsUpdate = zone.id !== currentZoneId || 
        (zone.shop_id && String(zone.shop_id) !== String(currentShopId)) ||
        (!currentBrand && zone.shop?.brand)
      
      if (needsUpdate) {
        const newSyncKey = `${zone.id}-${zone.shop_id || ''}-${zone.shop?.brand?.id || ''}`
        if (newSyncKey !== lastSyncKey) {
          lastSyncRef.current = { 
            zoneId: zone.id, 
            shopId: zone.shop_id || '', 
            brandId: String(zone.shop?.brand?.id || '') 
          }
          if (zone.id !== currentZoneId) state.setZoneId(zone.id)
          if (zone.shop_id && String(zone.shop_id) !== String(currentShopId)) state.setShopId(String(zone.shop_id))
          if (!currentBrand && zone.shop?.brand) state.setBrand(zone.shop.brand as Brand)
        }
      }
      return
    }

    // Если несколько зон и есть выбранный бренд
    if (currentBrand) {
      const zoneForBrand = deliveryZones.find(
        (z) => String(z.shop?.brand?.id) === String(currentBrand.id),
      )
      
      if (zoneForBrand) {
        const needsUpdate = zoneForBrand.id !== currentZoneId || 
          (zoneForBrand.shop_id && String(zoneForBrand.shop_id) !== String(currentShopId))
        
        if (needsUpdate) {
          const newSyncKey = `${zoneForBrand.id}-${zoneForBrand.shop_id || ''}-${currentBrand.id}`
          if (newSyncKey !== lastSyncKey) {
            lastSyncRef.current = { 
              zoneId: zoneForBrand.id, 
              shopId: zoneForBrand.shop_id || '', 
              brandId: String(currentBrand.id) 
            }
            if (zoneForBrand.id !== currentZoneId) state.setZoneId(zoneForBrand.id)
            if (zoneForBrand.shop_id && String(zoneForBrand.shop_id) !== String(currentShopId)) {
              state.setShopId(String(zoneForBrand.shop_id))
            }
          }
        }
      }
      return
    }
    
    // Если бренд не выбран и zoneId не установлен
    if (!currentZoneId && !currentBrand && deliveryZones[0]) {
      const firstZone = deliveryZones[0]
      const newSyncKey = `${firstZone.id}-${firstZone.shop_id || ''}-${firstZone.shop?.brand?.id || ''}`
      if (newSyncKey !== lastSyncKey) {
        lastSyncRef.current = { 
          zoneId: firstZone.id, 
          shopId: firstZone.shop_id || '', 
          brandId: String(firstZone.shop?.brand?.id || '') 
        }
        state.setZoneId(firstZone.id)
        if (firstZone.shop_id) state.setShopId(String(firstZone.shop_id))
        if (firstZone.shop?.brand) state.setBrand(firstZone.shop.brand as Brand)
      }
    }
  }, [ deliveryZones, deliveryType, loadingZones ])

  // Синхронизация shopId при изменении zoneId
  useEffect(() => {
    if (deliveryType === 'pickup') return
    if (loadingZones || deliveryZones.length === 0) return
    if (!zoneId) return

    const zone = deliveryZones.find((z) => z.id === zoneId)
    if (!zone) return

    const state = useAddressStore.getState()
    
    // Синхронизируем shopId с зоной
    if (zone.shop_id && String(zone.shop_id) !== String(state.shopId)) {
      state.setShopId(String(zone.shop_id))
    }
    
    // Синхронизируем brand с зоной
    if (zone.shop?.brand && String(zone.shop.brand.id) !== String(state.brand?.id)) {
      state.setBrand(zone.shop.brand)
    }
  }, [ zoneId, deliveryZones, deliveryType, loadingZones ])

  // Авто-выбор слота
  useEffect(() => {
    if (loadingSlots) return
    if (slots.length === 0) return
    
    const isRestricted = (slot: Slot | null) => 
      Boolean((slot as unknown as { isRestricted?: boolean }).isRestricted)
    
    const availableSlots = slots.filter((slot) => !isRestricted(slot))
    
    const selectedIsAvailable = selectedSlot
      ? availableSlots.some((s) => s.id === selectedSlot.id)
      : false

    if (!selectedSlot || !selectedIsAvailable) {
      const firstSlot = availableSlots[0] ?? slots[0]
      if (firstSlot && !isRestricted(firstSlot)) {
        setSelectedSlot(firstSlot)
      }
    }
  }, [ slots, selectedSlot, loadingSlots, setSelectedSlot ])

  return null
})

export default DeliveryManager
